import { lazy, Suspense } from 'react';
import {
  createRootRoute, createRoute, createRouter, Navigate, Outlet,
} from '@tanstack/react-router';
import type { Session } from '@prody/shared';
import { EmptyState, Button } from '~/design-system';
import { MODULES } from '~/modules';
import { BusinessProvider } from '~/platform/tenancy/BusinessProvider';
import { AppShell } from './shell/AppShell';
import { ModuleGuard } from './ModuleGuard';
import { businessPath, path } from './navigation';

/* ------------------------------------------------------------------ *
 * Routing
 *
 * `/b/:businessSlug/...` — the tenant is in the path, not in a store.
 * That is what lets two tenants live in two browser tabs and what
 * makes a pasted link open the right business for whoever clicks it.
 *
 * The route tree is generated from MODULES rather than written out.
 * A route exists for every module in the build; whether the current
 * tenant and role may *see* it is decided by ModuleGuard, so a
 * disabled module 404s honestly instead of rendering a blank shell.
 * ------------------------------------------------------------------ */

export const createAppRouter = (session: Session) => {
  const rootRoute = createRootRoute({
    component: () => <Outlet />,
    notFoundComponent: NotFound,
  });

  /** `/` → the user's first business. */
  const indexRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/',
    component: () => {
      const first = session.memberships[0]?.business.slug;
      return first ? <Navigate to={path(businessPath(first))} /> : <NoBusiness />;
    },
  });

  /** Reachable from the business switcher before any tenant exists. */
  const onboardingRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/onboarding',
    component: NoBusiness,
  });

  const businessRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/b/$businessSlug',
    component: function BusinessLayout() {
      const { businessSlug } = businessRoute.useParams();
      const membership = session.memberships.find((m) => m.business.slug === businessSlug);
      if (!membership) return <NotFound />;
      return (
        <BusinessProvider session={session} businessSlug={businessSlug}>
          <AppShell />
        </BusinessProvider>
      );
    },
  });

  const moduleRoutes = MODULES.flatMap((module) =>
    module.routes.map((route) => {
      const Component = lazy(route.component);
      return createRoute({
        getParentRoute: () => businessRoute,
        path: route.path,
        component: () => (
          <ModuleGuard moduleId={module.id} requires={route.requires ?? module.requires}>
            <Suspense fallback={<RouteFallback />}>
              <Component />
            </Suspense>
          </ModuleGuard>
        ),
      });
    }),
  );

  const routeTree = rootRoute.addChildren([
    indexRoute,
    onboardingRoute,
    businessRoute.addChildren(moduleRoutes),
  ]);

  return createRouter({
    routeTree,
    defaultPreload: 'intent',
    // The shell stays mounted across module changes, so a stale
    // module's data never flashes under a new module's chrome.
    defaultPendingMs: 120,
    scrollRestoration: true,
  });
};

/** Hatched placeholder while a module chunk loads. Still, never shimmering. */
function RouteFallback() {
  return (
    <div style={{ padding: 'var(--s-7)' }} aria-busy="true">
      <span className="hatch" style={{ display: 'block', blockSize: '1.4em', maxInlineSize: 260 }} />
    </div>
  );
}

function NotFound() {
  return (
    <div style={{ display: 'grid', placeContent: 'center', blockSize: '100dvh' }}>
      <EmptyState
        title="Not found"
        body="This page does not exist, or it belongs to another business."
        action={
          <Button variant="primary" onClick={() => window.history.back()}>
            Back
          </Button>
        }
      />
    </div>
  );
}

function NoBusiness() {
  return (
    <div style={{ display: 'grid', placeContent: 'center', blockSize: '100dvh' }}>
      <EmptyState
        title="Connect a business"
        body="Prody works per business. Create one, or ask a colleague to invite you to theirs."
        action={<Button variant="primary">Connect a business</Button>}
      />
    </div>
  );
}

declare module '@tanstack/react-router' {
  interface Register {
    router: ReturnType<typeof createAppRouter>;
  }
}
